import { Fragment, FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ProgressSpinner } from "primereact/progressspinner";
import { Tooltip } from "primereact/tooltip";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { translationKeys } from "../../../../models/app-translation-keys";
import { useStore } from "../../../../stores/StoreContext";
import { IProgressVisualizationProps } from "../ProgressHandler";

import "./ProgressCircle.scss";

interface IProgressCircleProps extends IProgressVisualizationProps {
  size?: number;
  strokeInnerRing?: number;
  strokeOuterRing?: number;
  showSpinner?: boolean;
  showInnerDescription?: boolean;
}

export const ProgressCircle: FunctionComponent<IProgressCircleProps> = (props: IProgressCircleProps) => {
  const { t } = useTranslation();
  const { userStore } = useStore();

  const numberOfProgressItems = props.progressValues.length;
  const showInnerDescription = !!props.showInnerDescription && numberOfProgressItems === 1;

  const defaultStroke = showInnerDescription ? 16 : 8;
  const defaultSize = showInnerDescription ? 140 : 60;

  const strokeInnerRing = props.strokeInnerRing || defaultStroke;
  const strokeOuterRing = props.strokeOuterRing || defaultStroke;

  const size = props.size || defaultSize;
  const radius = (size - strokeOuterRing) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const [cleanedProgressValues, setCleanedProgressValues] = useState<number[]>([]);
  const [totalProgressLength, setTotalProgressLength] = useState<number>(0);
  const [currentProgressIndex, setCurrentProgressIndex] = useState<number>(0);

  useEffect(() => {
    const cleanedValues = Array<number>();
    props.progressValues.forEach((value, index) => {
      if (index > 0 && value > 0) {
        cleanedValues[index - 1] = 100;
      }

      if ((index === 0 && value !== 100) || (index > 0 && props.progressValues[index - 1] === 100)) {
        setCurrentProgressIndex(index);
      }
      cleanedValues.push(value);
    });
    setCleanedProgressValues(cleanedValues);
  }, [props.progressValues]);

  useEffect(() => {
    const totalPercentage = cleanedProgressValues.reduce((a, b) => a + b, 0) / cleanedProgressValues.length / 100;
    const totalCircularLength = totalPercentage * circumference;
    setTotalProgressLength(totalCircularLength);
  }, [cleanedProgressValues]);

  const DescriptionInsideCircle = useCallback(
    () =>
      !!cleanedProgressValues?.length && (
        <g style={{ transform: `translate(${center}px, ${center}px)` }} className="progress-circle-inner-content">
          <g className="progress-description-inside">
            <g>
              <text textAnchor="middle" dy="-0.5rem" className="percentage">
                {cleanedProgressValues[0]?.toFixed(0)}%
              </text>
              <text textAnchor="middle" dy="0.8rem" className="title">
                {t(props.progressTitles[0])}
              </text>
            </g>

            {props.showSpinner && (
              <g style={{ transform: `translate(-0.8rem, 1.3rem)` }}>{ProgressAnimationSpinner(1.2)}</g>
            )}
          </g>
        </g>
      ),
    [cleanedProgressValues, props.progressTitles, props.showSpinner, userStore.currentLanguage],
  );

  const DescriptionOutsideCircle = useCallback(
    () => (
      <div className="progress-description-outside">
        {props.progressTitles
          .filter((_, index) => index <= currentProgressIndex)
          .map((title, index) => (
            <Fragment key={index}>
              <span className="percentage">{cleanedProgressValues[index]?.toFixed(0)}%</span>
              <span className="title">{t(title)}</span>
            </Fragment>
          ))}
      </div>
    ),
    [cleanedProgressValues, currentProgressIndex, props.progressTitles, userStore.currentLanguage],
  );

  const ProgressAnimationSpinner = useCallback(
    (sizeRem: number) => (
      <foreignObject width={`${sizeRem}rem`} height={`${sizeRem}rem`}>
        <ProgressSpinner
          className="multi-progress-spinner"
          style={{ width: `${sizeRem}rem`, height: `${sizeRem}rem` }}
        />
      </foreignObject>
    ),
    [userStore.currentLanguage],
  );

  const CancelButton = useCallback(
    (sizeRem: number) => (
      <g style={{ transform: `translate(-${sizeRem / 2}rem, -${sizeRem / 2}rem)` }} className="progress-cancel">
        <foreignObject width={`${sizeRem}rem`} height={`${sizeRem}rem`}>
          <Tooltip target=".progress-cancel" content={t(translationKeys.actions.cancelProgress)} position="top" />

          <FontAwesomeIcon
            icon={faXmark}
            onClick={props.cancelProgress}
            style={{ width: `${sizeRem}rem`, height: `${sizeRem}rem` }}
          />
        </foreignObject>
      </g>
    ),
    [userStore.currentLanguage],
  );

  if (isNaN(totalProgressLength)) {
    return;
  }

  return (
    <div className="progress-circle-container flex flex-row gap-3 align-items-center">
      <svg width={size} height={size} className="multi-progress-svg">
        <g
          className="multi-progress-circle"
          style={{
            transformOrigin: `${center}px ${center}px`,
            transform: `rotate(-90deg)`,
          }}
        >
          <radialGradient id="moving-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            {new Array(7).fill(true).map((_, index) => (
              <stop key={index} offset={`${(index / 6) * 100}%`} className={`color-stop-${index + 1}`} />
            ))}
          </radialGradient>

          <circle
            className="multi-progress-circle-background"
            strokeWidth={strokeOuterRing}
            fill="transparent"
            r={radius}
            cx={center}
            cy={center}
          />

          <circle
            className="multi-progress-circle-progress"
            strokeWidth={strokeInnerRing}
            stroke="#"
            fill="transparent"
            r={radius}
            cx={center}
            cy={center}
            strokeDasharray={`${totalProgressLength} ${circumference - totalProgressLength}`}
            style={{
              transformOrigin: `${center}px ${center}px`,
              transition: "stroke-dasharray 0.6s ease",
            }}
          />
        </g>

        {showInnerDescription ? (
          DescriptionInsideCircle()
        ) : (
          <g style={{ transform: `translate(${center}px, ${center}px)` }}>
            {props.showSpinner && (
              <g style={{ transform: `translate(-0.6rem, -0.6rem)` }}>{ProgressAnimationSpinner(1.2)}</g>
            )}
          </g>
        )}

        <g style={{ transform: `translate(${center}px, ${center}px)` }}>{CancelButton(2)}</g>
      </svg>

      {!showInnerDescription && DescriptionOutsideCircle()}
    </div>
  );
};
